import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Stack,
  CircularProgress,
  Paper,
  Button,
} from "@mui/material";
import { CloudUpload } from "@mui/icons-material";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import {
  CustomButton,
  CustomButtonWithBorder,
} from "components/CustomComponents/CustomButton";
import { useFetch } from "services/hooks/useFetch";
import { useSelector } from "react-redux";
import { themes } from "services/constants";
import Papa from "papaparse";
import { showToast } from "services/utils/Status";

const fallbackComponentTypeOptions = [
  { label: "Other Earnings", value: 1 },
  { label: "Other Deduction", value: 2 },
];

const fallbackComponentNameOptions = [];

const sampleCsvFileContent = `Employee Id,Employee Name,Amount,,,,,,,,
ORGAN1234567,Vijay,500,,,,,,,,
ORGAN1234567,Ajith,500,,,,,,,,
ORGAN1234567,vikram,500,,,,,,,,
ORGAN1234567,surya,500,,,,,,,,`;

const getResponseList = (response) =>
  (Array.isArray(response) && response) ||
  response?.data ||
  response?.results ||
  response?.components ||
  response?.component_name ||
  [];

const getComponentLabel = (item) => {
  if (typeof item === "string") {
    return item.trim();
  }

  return String(
    item?.component_name ||
      item?.name ||
      item?.label ||
      item?.component?.name ||
      "",
  ).trim();
};

export const BulkImportConfigure = ({ data, onUpdate, onNext, onClose }) => {
  const { domain, topbarCompanyId } = useSelector(
    (state) => state.tokenReducer,
  );

  const [componentNameOptions, setComponentNameOptions] = useState(
    fallbackComponentNameOptions,
  );
  const [selectedComponentType, setSelectedComponentType] = useState(
    data?.componentType || null,
  );
  const [selectedComponentName, setSelectedComponentName] = useState(
    data?.componentName || null,
  );
  const [fileUploading, setFileUploading] = useState(false);
  const payrollName = data?.payroll?.label || "the selected payroll run";

  const { result: componentNames, setLoad: reCallMemberApi } = useFetch(
    selectedComponentType
      ? `${domain}HRZone/Salary/Salary_Component/get-component-based-values/?component_category=${selectedComponentType.label}&company_id=${topbarCompanyId}`
      : null,
  );

  const componentTypeOptions = useMemo(() => fallbackComponentTypeOptions, []);

  useEffect(() => {
    const componentList = getResponseList(componentNames);

    if (componentList.length) {
      const mappedOptions = componentList
        .map((item) => {
          const label = getComponentLabel(item);

          if (!label) return null;

          return {
            label,
            value: item.id,
          };
        })
        .filter(Boolean);

      setComponentNameOptions(mappedOptions);
    } else if (selectedComponentType) {
      setComponentNameOptions(fallbackComponentNameOptions);
    }
  }, [componentNames, onUpdate, selectedComponentName, selectedComponentType]);

  const handleFileUpload = (event) => {
    const files = event.target.files;
    if (files && files[0]) {
      const file = files[0];

      const isCsvFile =
        file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");
      if (!isCsvFile) {
        showToast("error", "Invalid file format. Please upload a CSV file.");
        return;
      }

      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        showToast("error", "File size exceeds 10 MB limit");
        return;
      }

      setFileUploading(true);

      Papa.parse(file, {
        complete: function (results) {
          setFileUploading(false);
          onUpdate({
            csvFile: file,
            fileName: file.name,
            fileSize: (file.size / 1024).toFixed(2) + " KB",
            previewData: results.data.slice(0, 5),
          });
        },
        error: function (error) {
          setFileUploading(false);
          showToast("error", "Couldn't read the CSV file. Please check the format and try again.");
        },
      });
    }
  };

  const handleDownloadSample = () => {
    const element = document.createElement("a");
    const file = new Blob([sampleCsvFileContent], { type: "text/csv" });
    element.href = URL.createObjectURL(file);
    element.download = "Earning_BasicSalary_Sample.csv";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const isFormValid =
    selectedComponentType && selectedComponentName && data?.csvFile;

  return (
    <Box sx={{ p: { xs: 2, md: 0 } }}>
      <Box
        sx={{
          mb: 2,
          p: 3,
          borderRadius: 3,
          backgroundColor: themes?.whiteColor,
          border: `1px solid ${themes?.miliedLightGray}`,
          boxShadow: "0px 16px 34px rgba(15, 15, 15, 0.06)",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          What are you importing?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select the payroll run, component type and component name.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="caption"
              sx={{
                color: themes?.generalSubHeader,
                fontWeight: 700,
                mb: 1,
                display: "block",
                letterSpacing: "0.08em",
              }}
            >
              COMPONENT TYPE
            </Typography>
            <CustomAutoComplete
              dataList={componentTypeOptions}
              selectedValue={selectedComponentType}
              updateDataFunction={(event, newValue) => {
                setSelectedComponentType(newValue);
                setSelectedComponentName(null);
                onUpdate({ componentType: newValue, componentName: null });
                reCallMemberApi(true);
              }}
              placeHolder="Select component type"
              label=""
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography
              variant="caption"
              sx={{
                color: themes?.generalSubHeader,
                fontWeight: 700,
                mb: 1,
                display: "block",
                letterSpacing: "0.08em",
              }}
            >
              COMPONENT NAME
            </Typography>
            <CustomAutoComplete
              dataList={componentNameOptions}
              selectedValue={selectedComponentName}
              updateDataFunction={(event, newValue) => {
                setSelectedComponentName(newValue);
                onUpdate({ componentName: newValue });
              }}
              placeHolder="Select component name"
              label=""
              disableAction={!selectedComponentType}
            />
          </Grid>
        </Grid>

        {(selectedComponentType || selectedComponentName) && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 2,
              backgroundColor: "rgba(0, 212, 170, 0.08)",
              border: `1px solid rgba(0, 212, 170, 0.25)`,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              <Box
                component="span"
                sx={{
                  backgroundColor: "rgba(0, 212, 170, 0.16)",
                  color: themes?.stepActiveColor,
                  px: 1.25,
                  py: 0.4,
                  borderRadius: 1,
                  mr: 1,
                  fontWeight: 700,
                }}
              >
                {selectedComponentType?.label || "Component"}
              </Box>
              {selectedComponentName?.label || "Selection"} will be imported
              into {payrollName}. One amount per employee.
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          mb: 2,
          p: 3,
          borderRadius: 3,
          backgroundColor: themes?.whiteColor,
          border: `1px solid ${themes?.miliedLightGray}`,
          boxShadow: "0px 16px 34px rgba(15, 15, 15, 0.06)",
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Upload CSV file
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Required columns:{" "}
            <Box
              component="span"
              sx={{
                fontFamily: "monospace",
                px: 0.5,
                backgroundColor: "#f4f7fb",
                borderRadius: 1,
              }}
            >
              employee_id
            </Box>{" "}
            <Box
              component="span"
              sx={{
                fontFamily: "monospace",
                px: 0.5,
                backgroundColor: "#f4f7fb",
                borderRadius: 1,
              }}
            >
              amount
            </Box>{" "}
            <Box
              component="span"
              sx={{
                fontFamily: "monospace",
                px: 0.5,
                backgroundColor: "#f4f7fb",
                borderRadius: 1,
              }}
            >
              notes
            </Box>{" "}
            (optional)
          </Typography>
        </Box>

        <Box
          sx={{
            border: "2px dashed rgba(32, 124, 229, 0.4)",
            borderRadius: 2,
            p: 4,
            textAlign: "center",
            backgroundColor: "rgba(235, 245, 255, 0.8)",
            cursor: "pointer",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "rgba(227, 239, 255, 1)",
              borderColor: "rgba(32, 124, 229, 0.7)",
            },
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const files = e.dataTransfer.files;
            if (files) {
              const event = { target: { files } };
              handleFileUpload(event);
            }
          }}
        >
          {fileUploading ? (
            <Stack alignItems="center" spacing={2}>
              <CircularProgress size={40} />
              <Typography variant="body2">Processing file...</Typography>
            </Stack>
          ) : (
            <>
              <CloudUpload
                sx={{
                  fontSize: 48,
                  color: themes?.grayColor,
                  mb: 1,
                }}
              />
              <Typography variant="body1" sx={{ mb: 1 }}>
                Drag and drop your CSV here, or{" "}
                <Box
                  component="label"
                  sx={{
                    color: themes?.grayColor,
                    cursor: "pointer",
                    fontWeight: 700,
                    textDecoration: "underline",
                  }}
                >
                  select a file
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    style={{ display: "none" }}
                  />
                </Box>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Only CSV files — max 10 MB
              </Typography>
            </>
          )}
        </Box>

        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ color: themes?.generalSubHeader, fontSize: "0.85rem" }}>
            CSV only · max 10 MB · Employee name resolved automatically from ID
          </Box>
          <Button
            onClick={handleDownloadSample}
            startIcon={<CloudUpload sx={{ color: themes?.stepActiveColor }} />}
            sx={{
              color: themes?.stepActiveColor,
              textTransform: "none",
              fontSize: "0.9rem",
            }}
          >
            Download sample CSV
          </Button>
        </Box>

        {data?.fileName && (
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 2,
              backgroundColor: "rgba(0, 212, 170, 0.12)",
              border: `1px solid rgba(0, 212, 170, 0.25)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {data.fileName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {data.fileSize} · {data.previewData?.length || 0} rows detected
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() =>
                onUpdate({
                  csvFile: null,
                  fileName: "",
                  fileSize: "",
                  previewData: [],
                })
              }
              sx={{ textTransform: "none" }}
            >
              Remove
            </Button>
          </Paper>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
        <CustomButtonWithBorder
          actionName="Cancel"
          actionFuntion={onClose}
          customTitleSx={{ textTransform: "none" }}
        />
        <CustomButton
          actionName="Preview data →"
          actionFuntion={onNext}
          disableAction={!isFormValid}
          loading={false}
          customTitleSx={{ textTransform: "none", fontSize: "0.95rem" }}
        />
      </Box>
    </Box>
  );
};
